/**
 * Own the "which maneuver baseline is on screen" state for a maneuver page.
 *
 * Mirrors useBaselineDisplay (the straight-line picker) with three differences: it lists baselines
 * that have maneuver curves (including a performance baseline maneuvers were saved onto), it
 * publishes the selection to `maneuverBaselineStore` because the
 * maneuver views read stores rather than props, and there is one slot instead of two -- a second
 * reference curve would double every map track and timeseries line as well as the scatter.
 *
 * The picked CONFIG drives every surface. A maneuver's own config is deliberately not used to pick
 * a curve per row: a table of tacks compared against whatever config each happened to sail is a
 * different question from "how does this compare to our reference for THIS setup".
 *
 * Only the configs holding a fit for the page's current maneuver type are offered — a fit built
 * mostly from gybes has configs that cannot draw anything on a page showing tacks.
 */

import { createSignal, type Accessor } from 'solid-js';
import { user } from '../store/userStore';
import { debug as logDebug, error as logError } from '../utils/console';
import {
  listBaselines,
  fetchBaseline,
  deleteBaseline,
  readManeuverBaselineFit,
  type BaselineRow,
  type ManeuverBaselineFitJson,
} from '../services/baselinesService';
import { maneuverBaselineConfigsForType } from '../utils/maneuverBaselineFit';
import {
  readManeuverBaselineSession,
  writeManeuverBaselineSession,
  loadManeuverBaselineFromPersistentSettings,
  saveManeuverBaselineToPersistentSettings,
} from '../utils/maneuverBaselinePersistence';
import { maneuverBaselineStore } from '../store/maneuverBaselineStore';
import { maneuverBaselineLabel } from '../utils/maneuverBaselineLegend';
import { normalizePerformanceTargetColor } from '../utils/performanceTargetColor';
import { NO_BASELINE_OPTION } from '../components/modals/BaselineDisplayModal';

/** White: reads as a reference against the tack/TWS/VMG colour scales the maneuver views use. */
export const DEFAULT_MANEUVER_BASELINE_COLOR = '#ffffff';

export interface UseManeuverBaselineDisplayOptions {
  getClassName: Accessor<string>;
  getProjectId: Accessor<number | string>;
  /**
   * The maneuver type the page is showing. Used to offer only the configs that hold a fit for it —
   * without this the picker lists configs that draw nothing on the page the user is looking at.
   */
  getEventType: Accessor<string>;
  /** Called after the displayed baseline changes so the page can force a redraw. */
  onApplied?: () => void;
}

export function useManeuverBaselineDisplay(options: UseManeuverBaselineDisplayOptions) {
  const [showModal, setShowModal] = createSignal(false);
  const [availableBaselines, setAvailableBaselines] = createSignal<BaselineRow[]>([]);
  const [selectedKey, setSelectedKey] = createSignal(NO_BASELINE_OPTION);
  const [modalConfigs, setModalConfigs] = createSignal<string[]>([]);
  const [modalConfig, setModalConfig] = createSignal('');
  const [modalColor, setModalColor] = createSignal(DEFAULT_MANEUVER_BASELINE_COLOR);
  const [loading, setLoading] = createSignal(false);
  const [loadingAction, setLoadingAction] = createSignal<'select' | 'remove' | null>(null);
  const [error, setError] = createSignal<string | null>(null);

  const contextIds = () => {
    const className = options.getClassName();
    const projectId = Number(options.getProjectId());
    const rawUserId = (user() as { user_id?: unknown })?.user_id;
    const userId = typeof rawUserId === 'string' && rawUserId !== '' ? rawUserId : null;
    return { className, projectId, userId };
  };

  const persist = (selection: {
    baselineId: number | null;
    baselineName: string;
    baselineConfig: string;
    baselineColor?: string;
  }) => {
    writeManeuverBaselineSession(selection);
    const { className, projectId, userId } = contextIds();
    if (userId == null || !className || !projectId) { return; }
    void saveManeuverBaselineToPersistentSettings(userId, className, projectId, selection)
      .catch((err) => logDebug('[useManeuverBaselineDisplay] persist failed', err));
  };

  const publish = (fit: ManeuverBaselineFitJson | null, id: number | null, name: string, config: string) => {
    maneuverBaselineStore.setBaselineFit(fit);
    maneuverBaselineStore.setBaselineId(id);
    maneuverBaselineStore.setBaselineName(name);
    maneuverBaselineStore.setBaselineConfig(config);
    maneuverBaselineStore.bumpOverlayRevision();
  };

  const clearBaseline = (persistChoice = true) => {
    publish(null, null, '', '');
    if (persistChoice) {
      // Recorded explicitly, so "off" survives a reload instead of auto-selecting again.
      persist({ baselineId: null, baselineName: '', baselineConfig: '' });
    }
    options.onApplied?.();
  };

  /**
   * Load a baseline's curves and show them.
   *
   * `preferredConfig` is honoured when the baseline actually holds a fit for it; otherwise the first
   * config, so a restored selection whose config has since disappeared still draws something.
   *
   * `persistChoice` is false for a shared link: the link should draw that baseline without becoming
   * the viewer's saved selection.
   */
  const applyBaseline = async (id: number, preferredConfig?: string, persistChoice = true): Promise<boolean> => {
    const { className, projectId } = contextIds();
    if (!className || !projectId) { return false; }

    const row = await fetchBaseline(className, projectId, id);
    if (!row) {
      logDebug('[useManeuverBaselineDisplay] baseline no longer exists', id);
      clearBaseline(false);
      return false;
    }
    const fit = readManeuverBaselineFit(row.fit_json);
    if (!fit) {
      // Straight-line only, or members that produced no maneuver fit: nothing to draw here.
      logDebug('[useManeuverBaselineDisplay] no maneuver fit', id);
      clearBaseline(false);
      return false;
    }
    const configs = maneuverBaselineConfigsForType(fit, options.getEventType());
    const config = preferredConfig && configs.includes(preferredConfig)
      ? preferredConfig
      : (configs[0] || '');

    publish(fit, row.baseline_id, row.name, config);
    if (persistChoice) {
      persist({
        baselineId: row.baseline_id,
        baselineName: row.name,
        baselineConfig: config,
        baselineColor: maneuverBaselineStore.baselineColor(),
      });
    }
    options.onApplied?.();
    return true;
  };

  /**
   * Draw the baseline a share link named, without writing it into this viewer's saved selection.
   */
  const showSharedBaseline = async (raw: unknown): Promise<void> => {
    const selection = (raw ?? {}) as {
      baselineId?: number | null;
      baselineConfig?: string;
      baselineColor?: string;
    };
    if (typeof selection.baselineColor === 'string' && selection.baselineColor.trim() !== '') {
      maneuverBaselineStore.setBaselineColor(normalizePerformanceTargetColor(selection.baselineColor));
    }
    if (selection.baselineId == null) {
      clearBaseline(false);
      return;
    }
    try {
      await applyBaseline(selection.baselineId, selection.baselineConfig, false);
    } catch (err: unknown) {
      logError('[useManeuverBaselineDisplay] shared baseline failed', err);
    }
  };

  /**
   * Restore the previous choice on mount: session first, then persistent settings.
   *
   * Nothing is auto-selected without a saved preference — a baseline is a deliberate comparison, and
   * silently picking one would make the charts look different for no visible reason.
   */
  const restoreSavedBaseline = async (): Promise<void> => {
    const { className, projectId, userId } = contextIds();
    if (!className || !projectId) { return; }

    let saved = readManeuverBaselineSession();
    if (!saved && userId != null) {
      saved = await loadManeuverBaselineFromPersistentSettings(userId, className, projectId);
    }
    if (!saved) { return; }

    if (saved.baselineColor) { maneuverBaselineStore.setBaselineColor(saved.baselineColor); }
    if (saved.baselineId == null) { return; }   // explicit "No baseline"

    try {
      await applyBaseline(saved.baselineId, saved.baselineConfig);
    } catch (err: unknown) {
      logError('[useManeuverBaselineDisplay] restore failed', err);
    }
  };

  const refreshBaselineList = async (): Promise<BaselineRow[]> => {
    const { className, projectId } = contextIds();
    if (!className || !projectId) { return []; }
    const rows = await listBaselines(className, projectId, { kind: 'maneuver' });
    setAvailableBaselines(rows);
    return rows;
  };

  /** Configs offered for a given picker selection, without loading curves we may not keep. */
  const loadConfigsForKey = async (key: string) => {
    if (key === NO_BASELINE_OPTION) {
      setModalConfigs([]);
      setModalConfig('');
      return;
    }
    const id = Number(key);
    if (!Number.isInteger(id) || id <= 0) { return; }

    // The displayed baseline's fit is already in hand.
    if (id === maneuverBaselineStore.baselineId() && maneuverBaselineStore.baselineFit()) {
      const configs = maneuverBaselineConfigsForType(
        maneuverBaselineStore.baselineFit(),
        options.getEventType()
      );
      const current = maneuverBaselineStore.baselineConfig();
      setModalConfigs(configs);
      // Only keep the shown config preselected while it is still one of the offered ones.
      setModalConfig(current && configs.includes(current) ? current : (configs[0] || ''));
      return;
    }

    const { className, projectId } = contextIds();
    try {
      const row = await fetchBaseline(className, projectId, id);
      const fit = readManeuverBaselineFit(row?.fit_json);
      const configs = maneuverBaselineConfigsForType(fit, options.getEventType());
      setModalConfigs(configs);
      setModalConfig(configs[0] || '');
    } catch (err: unknown) {
      logError('[useManeuverBaselineDisplay] failed to load configs', err);
      setModalConfigs([]);
      setModalConfig('');
    }
  };

  const openModal = async () => {
    setError(null);
    setShowModal(true);
    setModalColor(maneuverBaselineStore.baselineColor());
    const current = maneuverBaselineStore.baselineId();
    const key = current != null ? String(current) : NO_BASELINE_OPTION;
    setSelectedKey(key);
    try {
      await refreshBaselineList();
      await loadConfigsForKey(key);
    } catch (err: unknown) {
      logError('[useManeuverBaselineDisplay] failed to list baselines', err);
      setError('Could not load baselines.');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setError(null);
  };

  const handleSelectedKeyChange = (value: string) => {
    setSelectedKey(value);
    setError(null);
    void loadConfigsForKey(value);
  };

  const handleConfirm = async () => {
    setError(null);
    const key = selectedKey();
    maneuverBaselineStore.setBaselineColor(modalColor());

    if (key === NO_BASELINE_OPTION) {
      clearBaseline();
      closeModal();
      return;
    }

    const id = Number(key);
    if (!Number.isInteger(id) || id <= 0) {
      setError('Select a baseline.');
      return;
    }

    setLoading(true);
    setLoadingAction('select');
    try {
      const ok = await applyBaseline(id, modalConfig());
      if (!ok) {
        setError('That baseline could not be loaded.');
        return;
      }
      closeModal();
    } catch (err: unknown) {
      logError('[useManeuverBaselineDisplay] select failed', err);
      setError(err instanceof Error ? err.message : 'Could not load that baseline.');
    } finally {
      setLoading(false);
      setLoadingAction(null);
    }
  };

  const handleRemove = async () => {
    const id = Number(selectedKey());
    if (!Number.isInteger(id) || id <= 0) { return; }

    const name = availableBaselines().find((b) => b.baseline_id === id)?.name || 'this baseline';
    if (typeof window !== 'undefined'
      && !window.confirm(`Delete "${name}"? This removes the baseline for everyone on the project.`)) {
      return;
    }

    setLoading(true);
    setLoadingAction('remove');
    try {
      const { className, projectId } = contextIds();
      const result = await deleteBaseline(className, projectId, id);
      if (!result.success) {
        setError(result.message || 'Could not delete that baseline.');
        return;
      }
      if (maneuverBaselineStore.baselineId() === id) { clearBaseline(); }
      await refreshBaselineList();
      setSelectedKey(NO_BASELINE_OPTION);
      setModalConfigs([]);
      setModalConfig('');
    } catch (err: unknown) {
      logError('[useManeuverBaselineDisplay] delete failed', err);
      setError(err instanceof Error ? err.message : 'Could not delete that baseline.');
    } finally {
      setLoading(false);
      setLoadingAction(null);
    }
  };

  /** Show a baseline after a save, so newly added members appear immediately. */
  const refreshAfterSave = async (id: number, name: string) => {
    logDebug('[useManeuverBaselineDisplay] refreshing after save', { id, name });
    await applyBaseline(id, maneuverBaselineStore.baselineConfig());
  };

  /** What the legend and the settings row show. */
  const baselineLabel = () => maneuverBaselineLabel();

  return {
    baselineId: maneuverBaselineStore.baselineId,
    baselineName: maneuverBaselineStore.baselineName,
    baselineConfig: maneuverBaselineStore.baselineConfig,
    baselineColor: maneuverBaselineStore.baselineColor,
    baselineFit: maneuverBaselineStore.baselineFit,
    baselineLabel,

    showModal,
    availableBaselines,
    selectedKey,
    setSelectedKey: handleSelectedKeyChange,
    modalConfigs,
    modalConfig,
    setModalConfig,
    modalColor,
    setModalColor,
    loading,
    loadingAction,
    error,

    openModal,
    closeModal,
    handleConfirm,
    handleRemove,
    restoreSavedBaseline,
    showSharedBaseline,
    refreshAfterSave,
  };
}
